import React, { useState } from 'react';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { PendingApplicationAction } from './ReviewApplicationDialog';
import { getAddress, getDisplayName, getPhone } from './infiniteApplicationReview.utils';

export default function ApplicationActionConfirmDialog({
  pendingAction,
  open,
  onClose,
  onConfirm,
  isMutating,
}: {
  pendingAction: PendingApplicationAction | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (action: PendingApplicationAction, notes?: string) => void;
  isMutating: boolean;
}) {
  const [notes, setNotes] = useState('');
  const actionConfig = {
    APPROVE: {
      color: 'success',
      dialogTitle: 'Approve application',
      icon: <CheckCircleIcon />,
      label: 'Ready to approve',
      noteLabel: 'Approval note',
      verb: 'approve',
    },
    REJECT: {
      color: 'error',
      dialogTitle: 'Reject application',
      icon: <CancelIcon />,
      label: 'Ready to reject',
      noteLabel: 'Rejection note',
      verb: 'reject',
    },
    REOPEN: {
      color: 'info',
      dialogTitle: 'Reopen application',
      icon: <ReplayIcon />,
      label: 'Ready to reopen',
      noteLabel: 'Reopen note',
      verb: 'reopen',
    },
  } as const;
  const config = pendingAction ? actionConfig[pendingAction.action] : actionConfig.APPROVE;

  const close = () => {
    setNotes('');
    onClose();
  };

  const submit = () => {
    if (!pendingAction) {
      return;
    }
    onConfirm(pendingAction, notes.trim() || undefined);
    setNotes('');
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>{config.dialogTitle}</DialogTitle>
      <DialogContent>
        {pendingAction && (
          <Stack spacing={2}>
            <Chip
              color={config.color}
              icon={config.icon}
              label={config.label}
              sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
            />
            <Typography variant="body1">You are about to {config.verb} the application from:</Typography>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {getDisplayName(pendingAction.applicant)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {pendingAction.applicant.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getPhone(pendingAction.applicant)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getAddress(pendingAction.applicant) || '-'}
              </Typography>
            </Box>
            <Typography variant="body2">Are you sure?</Typography>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                Current note
              </Typography>
              <Typography
                variant="body2"
                color={pendingAction.currentNotes ? 'text.primary' : 'text.secondary'}
                sx={{ whiteSpace: 'pre-wrap' }}
              >
                {pendingAction.currentNotes || 'No notes recorded.'}
              </Typography>
            </Box>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={3}
              margin="dense"
              label={config.noteLabel}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              helperText="Optional"
            />
            {pendingAction.action === 'APPROVE' && (
              <Typography variant="caption" color="text.secondary">
                This note will be added to the approval decision.
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={isMutating}>
          Cancel
        </Button>
        <Button color={config.color} variant="contained" disabled={!pendingAction || isMutating} onClick={submit}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
